import React, { memo, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTheme, createUseStyles, Icon, Button, Text, Modal } from '@Sage/hoi-poi-ui';

import { downloadURI } from '/lib/helpers';
import { useWeb5Context } from 'lib/Web5Context';
import { getImports, deleteImports, getImport } from 'lib/services/imports';
import { showErrorToast } from 'lib/errors';
import { subscribe } from 'lib/EventBuser';
import { REFRESH_IMPORT_LIST } from 'lib/events';

import SectionList from 'components/SectionList';
import SectionToolbar from 'components/SectionToolbar';

import ImportsTabs from './ImportsTabs';
import FileNameFormatter from './formatters/FileNameFormatter';
import ImportsIcon from '../icons/ImportsIcon';
import { intercomHideForList } from 'utils/custom';
import { errorToast } from 'utils/toast';

import styles from './styles';
const useStyles = createUseStyles(styles, 'ImportsLists');

const ImportsLists = ({
    entityName,
    activeTab,
    activeShrinkedTab,
    onChangeView,
    onChangeTab,
    onChangeShrinked,
    onExport,
    onShowErrors,
    entityLogName,
    idSetupRole,
    activeRowId,
    setActiveRowId,
    isNotAdmin,
    setIsEdit,
}) => {
    const theme = useTheme();
    const classes = useStyles();
    const { t, logEvent } = useWeb5Context();
    const [lists, setLists] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setError] = useState(false);
    const [intercomRef, setIntercomRef] = useState('show');

    // Edit action
    const onEdit = useCallback(
        (row) => {
            onChangeView('import', row);
            setIsEdit(true);
            logEvent({
                event: entityLogName,
                submodule: entityName,
                functionality: 'edit',
            });
        },
        [onChangeView, setIsEdit, entityLogName, entityName, logEvent],
    );

    // Download action
    const onDownload = useCallback(
        (row) => {
            getImport(row.id)
                .then((result) => {
                    if (result.data.info && result.data.info.url) {
                        logEvent({
                            event: entityLogName,
                            submodule: entityName,
                            functionality: 'download',
                        });
                        downloadURI(result.data.info.url, result.data.info.key);
                    } else {
                        errorToast({
                            t,
                            title: t('cfm_label_error_generic'),
                        });
                    }
                })
                .catch((error) => {
                    showErrorToast(t, error);
                });
        },
        [entityName, entityLogName, logEvent, t],
    );

    // Remove action
    const [removeModal, setRemoveModal] = useState({ isLoading: false, isOpen: false, id: null });

    const onCloseRemoveModal = useCallback(() => {
        setActiveRowId(null);
        setRemoveModal({ isLoading: false, isOpen: false, id: null });
    }, [setRemoveModal, setActiveRowId]);

    const onRemove = useCallback(
        (row) => {
            setActiveRowId(row.id);
            setRemoveModal({ isLoading: false, isOpen: true, id: row.id });
        },
        [setRemoveModal, setActiveRowId],
    );

    const onConfirmRemoveModal = useCallback(() => {
        setRemoveModal({ isLoading: true, isOpen: true, id: removeModal.id });
        deleteImports(removeModal.id)
            .then(() => {
                logEvent({
                    event: entityLogName,
                    submodule: entityName,
                    functionality: 'delete',
                });
                let list = lists[entityName].filter((row) => row.id !== removeModal.id);
                setLists({ ...lists, [entityName]: list });
            })
            .catch((error) => {
                showErrorToast(t, error);
            })
            .then(() => {
                setActiveRowId(null);
                onCloseRemoveModal();
            });
    }, [
        removeModal.id,
        logEvent,
        entityLogName,
        entityName,
        lists,
        t,
        onCloseRemoveModal,
        setActiveRowId,
    ]);

    const getInfoForTag = useCallback(
        ({ state, resultOK, resultNOK, resultWOK }) => {
            switch (state) {
                case 0:
                case 1: // fields matched -> pending to start
                case 2:
                case 3:
                    return { value: t('cfm_label_event_draft'), type: 'pending' };
                case 4:
                    return { value: t('cfm_label_status_wip'), type: 'ongoing' };
                case 5:
                    const ok = resultOK === -1 ? 0 : resultOK;
                    const nok = resultNOK;
                    const wok = resultWOK;
                    const total = ok + nok;
                    if (nok !== 0) {
                        if (wok) {
                            const literal =
                                wok > 1
                                    ? 'cfm_label_import_warning_numbers'
                                    : 'cfm_label_import_warning_number';
                            return {
                                value: t('cfm_label_imports_values', [ok, total]),
                                type: 'error',
                                extraTags: [
                                    {
                                        value: t(literal, [wok]),
                                        type: 'warning',
                                    },
                                ],
                            };
                        }
                        return {
                            value: t('cfm_label_imports_values', [ok, total]),
                            type: 'error',
                        };
                    } else if (!nok && wok) {
                        return {
                            value: t('cfm_label_imports_values', [ok, total]),
                            type: 'warning',
                        };
                    } else {
                        return {
                            value: t('cfm_label_imports_values', [ok, total]),
                            type: 'success',
                        };
                    }
                default:
                    return {};
            }
        },
        [t],
    );

    // List configuration
    const columns = useMemo(
        () => [
            {
                colId: 'name',
                field: 'name',
                headerName: t('cfm_label_file_name'),
                pinned: 'left',
                cellRenderer: 'FileNameFormatter',
                resizable: true,
                width: 350,
                suppressSizeToFit: true,
                cellRendererParams: {
                    onDownload,
                },
            },
            {
                colId: 'type',
                field: 'type',
                headerName: t('cfm_label_import_type'),
                cellRenderer: 'textCell',
                width: 150,
                resizable: true,
                suppressSizeToFit: true,
                sortable: false,
            },
            {
                colId: 'date',
                field: 'date',
                headerName: t('cfm_label_date_uploaded'),
                cellRenderer: 'dateCell',
                cellRendererParams: {
                    inputFormat: "yyyy-MM-dd'T'HH:mm:ss",
                },
                width: 200,
                resizable: true,
                suppressSizeToFit: true,
                sortable: false,
            },
            {
                colId: 'state',
                field: 'state',
                headerName: t('cfm_label_success_imports'),
                cellRenderer: 'tagCell',
                cellRendererParams: {
                    innerMethods: {
                        getInfoForTag: getInfoForTag,
                    },
                    align: 'center',
                    alignHeader: 'center',
                },
                width: 200,
                resizable: true,
                suppressSizeToFit: true,
                sortable: false,
            },
            {
                colId: 'userName',
                field: 'userName',
                headerName: t('cfm_label_user'),
                cellRenderer: 'textCell',
                resizable: true,
            },
        ],
        [getInfoForTag, onDownload, t],
    );

    const listOptions = useMemo(
        () => ({
            onRowDoubleClicked: ({ data }) => {
                if (data.state > 3) return;
                onEdit(data);
            },
        }),
        [onEdit],
    );

    const actions = useMemo(() => {
        return [
            (row) => {
                if (row.state < 4)
                    return {
                        icon: (
                            <Icon
                                className="imports__edit-btn"
                                onClick={() => onEdit(row)}
                                name="edit"
                            />
                        ),
                        tooltip: { content: t('cfm_action_edit') },
                    };
            },
            (row) => {
                if (row.state === 5 && (row.resultNOK || row.resultWOK))
                    return {
                        icon: (
                            <Icon
                                className="imports__duplicate-btn"
                                onClick={() => {
                                    logEvent({
                                        event: entityLogName,
                                        submodule: entityName,
                                        functionality: 'viewLogs',
                                    });
                                    onShowErrors(row);
                                }}
                                name="download"
                            />
                        ),
                        tooltip: { content: t('cfm_action_download_errors_list') },
                    };
            },
            (row) => {
                if (row.state < 4)
                    return {
                        icon: <Icon onClick={() => onRemove(row)} name="trash" />,
                        tooltip: { content: t('cfm_action_delete') },
                    };
            },
        ];
    }, [entityName, entityLogName, logEvent, onEdit, onShowErrors, onRemove, t]);

    const toolbarItems = useMemo(() => {
        let finalToolbarItems = [];
        if (!isNotAdmin) {
            finalToolbarItems.push({
                component: (
                    <Button
                        type="terciary"
                        onClick={() => onExport(entityName, null, true)}
                        className="imports__toolbar__export-btn"
                    >
                        {t('cfm_action_export')}{' '}
                        {activeTab ? t(`cfm_label_${activeTab}`) : activeShrinkedTab?.label}
                    </Button>
                ),
                position: 'right',
            });
        }

        finalToolbarItems.push({
            component: (
                <Button
                    onClick={() => onChangeView('import')}
                    className="imports__toolbar__import-btn"
                >
                    {t('cfm_action_import')}{' '}
                    {activeTab ? t(`cfm_label_${activeTab}`) : activeShrinkedTab?.label}
                </Button>
            ),
            position: 'right',
        });

        return finalToolbarItems;
    }, [activeShrinkedTab, activeTab, entityName, onChangeView, onExport, t, isNotAdmin]);

    const getImportsList = useCallback(() => {
        return getImports()
            .then(({ data }) => {
                if (!data) return setError(true);
                setLists(
                    data.reduce((reduced, item) => {
                        item['userName'] = item.user.name;
                        item.type = item.type ? t(`cfm_label_${item.type}_import`) : '';
                        if (!reduced[item.resource]) reduced[item.resource] = [];
                        reduced[item.resource].push(item);
                        return reduced;
                    }, {}),
                );
            })
            .catch((err) => {
                console.error(err);
                setError(true);
            })
            .finally(() => setIsLoading(false));
    }, [t]);

    useEffect(() => {
        if (lists || isLoading || hasError) return;
        setIsLoading(true);
        getImportsList();
    }, [lists, isLoading, t, activeTab, activeShrinkedTab, hasError, getImportsList]);

    useEffect(() => {
        return subscribe(REFRESH_IMPORT_LIST, () => {
            getImportsList();
        });
    });

    const list = useMemo(() => {
        if (lists) return lists[entityName] || [];
        else null;
    }, [lists, entityName]);

    const emptyIcon = useMemo(() => {
        return <ImportsIcon color={theme.colors.grey[100]} />;
    }, [theme]);

    const getRowNodeId = useCallback((data) => {
        return data.id;
    }, []);

    const getActiveRowId = useCallback(() => {
        return activeRowId;
    }, [activeRowId]);

    const onCellMouseOver = useCallback(
        (data) => {
            const hideShow = intercomHideForList(data, list);
            setIntercomRef(hideShow);
        },
        [list],
    );

    return (
        <div className={classes.root}>
            <ImportsTabs
                onChange={onChangeTab}
                onChangeShrinked={onChangeShrinked}
                activeShrinkedTab={activeShrinkedTab}
                activeTab={activeTab}
                idSetupRole={idSetupRole}
                isNotAdmin={isNotAdmin}
            />
            <SectionToolbar items={toolbarItems} />
            <div className={classes.listContainer}>
                <SectionList
                    id="imports-list"
                    onCellMouseOver={onCellMouseOver}
                    className={classes.list}
                    columns={columns}
                    rows={list}
                    actions={actions}
                    isLoading={isLoading}
                    hasError={hasError}
                    components={{
                        FileNameFormatter,
                    }}
                    options={listOptions}
                    emptyViewProps={{ icon: emptyIcon }}
                    useSort={true}
                    getRowNodeId={getRowNodeId}
                    activeRowId={getActiveRowId()}
                    intercomRef={intercomRef}
                    useLoaderPlaceholder
                />
            </div>
            <Modal
                title={t('cfm_label_confirm_delete_import')}
                size="small"
                isOpen={removeModal.isOpen}
                onRequestClose={onCloseRemoveModal}
                onCancel={onCloseRemoveModal}
                onConfirm={onConfirmRemoveModal}
                cancelText={t('cfm_action_cancel')}
                confirmText={t('cfm_action_accept')}
                overrides={{
                    confirmButton: {
                        type: 'primary-error',
                        isLoading: removeModal.isLoading,
                    },
                }}
            >
                <Text>{t('cfm_label_confirm_delete_import_desc')}</Text>
            </Modal>
        </div>
    );
};

ImportsLists.propTypes = {
    entityName: PropTypes.string.isRequired,
    activeTab: PropTypes.string,
    activeShrinkedTab: PropTypes.object,
    onChangeView: PropTypes.func,
    onChangeTab: PropTypes.func,
    onChangeShrinked: PropTypes.func,
};

export default memo(ImportsLists);
